'use client';

import React, { useId } from 'react';

interface NumberInputProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  prefix?: string;
  suffix?: string;
  helpText?: string;
  required?: boolean;
}

export function NumberInput({
  label,
  value,
  onChange,
  min = 0,
  max,
  step = 1,
  prefix,
  suffix,
  helpText,
  required = false
}: NumberInputProps) {
  const id = useId();
  return (
    <div className="mb-4">
      <label htmlFor={id} className="block text-sm font-semibold text-gray-700 mb-2">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
            {prefix}
          </span>
        )}
        <input
          id={id}
          type="number"
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          min={min}
          max={max}
          step={step}
          aria-describedby={helpText ? `${id}-help` : undefined}
          className={`w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors ${
            prefix ? 'pl-8' : ''
          } ${suffix ? 'pr-12' : ''}`}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
            {suffix}
          </span>
        )}
      </div>
      {helpText && <p id={`${id}-help`} className="text-xs text-gray-500 mt-1">{helpText}</p>}
    </div>
  );
}

interface SelectInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  helpText?: string;
  required?: boolean;
}

export function SelectInput({
  label,
  value,
  onChange,
  options,
  helpText,
  required = false
}: SelectInputProps) {
  const id = useId();
  return (
    <div className="mb-4">
      <label htmlFor={id} className="block text-sm font-semibold text-gray-700 mb-2">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-describedby={helpText ? `${id}-help` : undefined}
        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {helpText && <p id={`${id}-help`} className="text-xs text-gray-500 mt-1">{helpText}</p>}
    </div>
  );
}

interface MultiSelectProps {
  label: string;
  value: string[];
  onChange: (value: string[]) => void;
  options: { value: string; label: string; description?: string }[];
  helpText?: string;
}

export function MultiSelect({ label, value, onChange, options, helpText }: MultiSelectProps) {
  const groupId = `multiselect-${label.toLowerCase().replace(/\s+/g, '-')}`;
  const toggleOption = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  return (
    <div className="mb-4">
      <label className="block text-sm font-semibold text-gray-700 mb-2">{label}</label>
      <div className="space-y-2">
        {options.map((option) => (
          <label
            key={option.value}
            className="flex items-start gap-3 p-3 border-2 border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
          >
            <input
              type="checkbox"
              checked={value.includes(option.value)}
              onChange={() => toggleOption(option.value)}
              aria-describedby={helpText ? `${groupId}-help` : undefined}
              className="mt-1 w-5 h-5 text-blue-500 border-gray-300 rounded focus:ring-2 focus:ring-blue-200"
            />
            <div className="flex-1">
              <div className="font-medium text-gray-900">{option.label}</div>
              {option.description && (
                <div className="text-sm text-gray-600 mt-1">{option.description}</div>
              )}
            </div>
          </label>
        ))}
      </div>
      {helpText && <p id={`${groupId}-help`} className="text-xs text-gray-500 mt-2">{helpText}</p>}
    </div>
  );
}

interface TextInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  helpText?: string;
  required?: boolean;
  placeholder?: string;
}

export function TextInput({
  label,
  value,
  onChange,
  helpText,
  required = false,
  placeholder
}: TextInputProps) {
  const id = useId();
  return (
    <div className="mb-4">
      <label htmlFor={id} className="block text-sm font-semibold text-gray-700 mb-2">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-describedby={helpText ? `${id}-help` : undefined}
        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors"
      />
      {helpText && <p id={`${id}-help`} className="text-xs text-gray-500 mt-1">{helpText}</p>}
    </div>
  );
}

interface SliderInputProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  helpText?: string;
}

export function SliderInput({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  unit = '',
  helpText
}: SliderInputProps) {
  const id = useId();
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <label htmlFor={id} className="block text-sm font-semibold text-gray-700">{label}</label>
        <span className="text-sm font-bold text-blue-600">
          {value}
          {unit}
        </span>
      </div>
      <input
        id={id}
        type="range"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        min={min}
        max={max}
        step={step}
        aria-describedby={helpText ? `${id}-help` : undefined}
        aria-valuetext={`${value}${unit}`}
        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
      />
      <div className="flex justify-between text-xs text-gray-500 mt-1">
        <span>
          {min}
          {unit}
        </span>
        <span>
          {max}
          {unit}
        </span>
      </div>
      {helpText && <p id={`${id}-help`} className="text-xs text-gray-500 mt-1">{helpText}</p>}
    </div>
  );
}

interface DualInputWithSliderProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  prefix?: string;
  suffix?: string;
  helpText?: string;
  required?: boolean;
}

export function DualInputWithSlider({
  label,
  value,
  onChange,
  min,
  max,
  prefix,
  suffix,
  helpText,
  required = false
}: DualInputWithSliderProps) {
  const id = useId();
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const formatDisplayValue = (num: number) => {
    if (prefix === '$' && num >= 1000000) {
      return `$${(num / 1000000).toFixed(1)}M`;
    }
    return `${prefix || ''}${formatNumber(num)}${suffix || ''}`;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remove commas and parse
    const cleanValue = e.target.value.replace(/,/g, '');
    const numValue = parseFloat(cleanValue) || 0;
    onChange(Math.max(min, Math.min(max, numValue)));
  };

  // Logarithmic scale conversion
  const valueToSliderPosition = (val: number): number => {
    // Handle zero or very small values
    if (val <= 0 || min <= 0) {
      return 0;
    }
    // Convert actual value to logarithmic slider position (0-100)
    const minLog = Math.log(Math.max(1, min));
    const maxLog = Math.log(max);
    const valueLog = Math.log(val);
    return ((valueLog - minLog) / (maxLog - minLog)) * 100;
  };

  const sliderPositionToValue = (position: number): number => {
    // Handle zero position
    if (position <= 0) {
      return min;
    }
    // Convert logarithmic slider position (0-100) to actual value
    const minLog = Math.log(Math.max(1, min));
    const maxLog = Math.log(max);
    const valueLog = minLog + (position / 100) * (maxLog - minLog);
    return Math.round(Math.exp(valueLog));
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const position = parseFloat(e.target.value);
    const newValue = sliderPositionToValue(position);
    onChange(newValue);
  };

  return (
    <div className="mb-4">
      <label htmlFor={id} className="block text-sm font-semibold text-gray-700 mb-2">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="relative mb-3">
        {prefix && (
          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
            {prefix}
          </span>
        )}
        <input
          id={id}
          type="text"
          value={formatNumber(value)}
          onChange={handleInputChange}
          aria-describedby={helpText ? `${id}-help` : undefined}
          className={`w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors ${
            prefix ? 'pl-8' : ''
          } ${suffix ? 'pr-12' : ''}`}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
            {suffix}
          </span>
        )}
      </div>
      <input
        type="range"
        value={valueToSliderPosition(value)}
        onChange={handleSliderChange}
        aria-label={`${label} slider`}
        min={0}
        max={100}
        step={0.1}
        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
      />
      <div className="flex justify-between text-xs text-gray-500 mt-1">
        <span>{formatDisplayValue(min)}</span>
        <span>{formatDisplayValue(max)}</span>
      </div>
      {helpText && <p id={`${id}-help`} className="text-xs text-gray-500 mt-1">{helpText}</p>}
    </div>
  );
}
